import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Autocomplete, TextField, Paper, IconButton, Modal } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { pageBoxSx, headerRowSx, leftTextSx, createButtonWrapperSx, controlsRowSx, allSelectCombinedSx, nodeDetailsSx, dispatcherSx, addAccountSX } from './LEStations.styles';
import { nodeNameSx, nodeCitySx } from '../node/operatingNodes.styles';
import { activeTagSx, inactiveTagSx, powerIconSx, activeDotColor, inactiveDotColor } from '../node/operatingNodes.styles';
import editIcon from '../../assets/edit-icon.svg';
import leStationIcon from '../../assets/le-station-icon.png';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Map, { Marker, type MapRef } from 'react-map-gl/mapbox';
import { getLEStations, setLEStationActive } from '../../services/leStationService';
import AlertTriangle from '../../assets/AlertTriangle.svg';
import { modalContainerSx, modalHeaderSx, modalTitleSx, modalCloseIconSx, modalContentSx, modalWarningRowSx, modalCancelButtonSx, modalDeactivateButtonSx, modalBodyTextSx } from '../node/createNode.styles';

type LEStation = {
  id: number;
  stationName: string;
  primaryContact: string;
  email: string;
  status: 'Active' | 'Inactive';
  jurisdiction: string;
  latitude?: number | null;
  longitude?: number | null;
  radius?: number | null;
};

const LEStation: React.FC<{ onAdd?: () => void; onEdit?: (row: any) => void; onImported?: (msg?: string) => void; notification?: { show: boolean; message?: string; severity?: 'success' | 'error' } }> = ({ onAdd, onEdit, onImported, notification }) => {
  const [rowsData, setRowsData] = useState<LEStation[]>([]);
  const [filteredRows, setFilteredRows] = useState<LEStation[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  // jurisdiction — Autocomplete that searches the API
  const [jurisdictionSearch, setJurisdictionSearch] = useState<string>('');
  const [jurisdictionOptions, setJurisdictionOptions] = useState<string[]>([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState<string | null>(null);
  const jurisdictionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
  const [powerState, setPowerState] = useState<Record<string, boolean>>({});
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN ?? '';
  const isValidMapboxToken = Boolean(mapboxToken && mapboxToken.startsWith('pk.'));
  const mapRef = React.useRef<MapRef | null>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchStations = async () => {
      setLoading(true);
      try {
        const apiPage = paginationModel.page + 1; // API is 1-based
        const apiPageSize = paginationModel.pageSize || pageSize;
        const searchParam = selectedJurisdiction || jurisdictionSearch || undefined;
        const activeParam = selectedStatus === 'Active' ? true : selectedStatus === 'Inactive' ? false : undefined;
        const { stations, meta } = await getLEStations(apiPage, apiPageSize, searchParam, activeParam);
        const mapped: LEStation[] = stations.map((s: any) => ({
          id: s.id,
          stationName: s.name,
          primaryContact: s.primary_contact,
          email: s.email,
          status: s.active ? 'Active' : 'Inactive',
          jurisdiction: s.district,
          // support multiple possible API field names for coordinates/radius
          latitude: s.latitude ?? s.lat ?? null,
          longitude: s.longitude ?? s.lng ?? s.lon ?? null,
          radius: s.geofence_radius_m ?? s.radius ?? null,
        }));

        setRowsData(mapped);
        // derive jurisdiction options for autocomplete from current page results
        const jurisSet = new Set<string>();
        mapped.forEach((r) => { if (r.jurisdiction) jurisSet.add(r.jurisdiction); });
        setJurisdictionOptions(Array.from(jurisSet));
        // client-side status filter
        setFilteredRows(mapped.filter(r => !selectedStatus || r.status === selectedStatus));
        setRowCount(meta?.total ?? mapped.length);
        setPowerState((prev) => {
          const next = { ...prev };
          mapped.forEach((r) => { next[r.stationName] = r.status === 'Active'; });
          return next;
        });
      } catch (err: any) {
        const msg = err?.message || 'Failed to fetch stations';
        window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } }));
        setRowsData([]);
        setRowCount(0);
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    fetchStations();

    return () => {
      aborted = true;
      controller.abort();
    };
  }, [paginationModel.page, paginationModel.pageSize, selectedJurisdiction, jurisdictionSearch, selectedStatus]);

  // re-apply client-side status filter when selection changes
  useEffect(() => {
    setFilteredRows(rowsData.filter(r => !selectedStatus || r.status === selectedStatus));
  }, [rowsData, selectedStatus]);

  useEffect(() => {
    const container = gridContainerRef.current;
    if (!container) return;
    const scroller = container.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement | null;
    if (!scroller) return;

    const check = () => {
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      if (rowsData.length <= (paginationModel.pageSize ?? pageSize)) {
        scroller.style.paddingBottom = '56px';
      } else {
        scroller.style.paddingBottom = atBottom ? '56px' : '0px';
      }
    };

    check();
    scroller.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      scroller.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [rowsData.length, paginationModel.pageSize, pageSize]);

  const openJurisdictionMap = async (jurisdiction: string) => {
    if (!jurisdiction) return;
    if (!isValidMapboxToken) {
      setMapError('Map disabled: invalid or missing Mapbox token.');
      setMapOpen(true);
      return;
    }
    setMapLoading(true);
    setMapError(null);
    try {
      const q = encodeURIComponent(jurisdiction);
      const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
      const url = `${host}/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxToken}&autocomplete=false&limit=1`;
      const res = await fetch(url);
      if (!res.ok) {
        setMapError('Failed to geocode jurisdiction');
        setMapCenter(null);
        setMapOpen(true);
        return;
      }
      const body = await res.json().catch(() => null);
      const feat = Array.isArray(body?.features) && body.features[0];
      if (!feat) {
        setMapError('Location not found');
        setMapCenter(null);
        setMapOpen(true);
        return;
      }
      const [lng, lat] = feat.center || [null, null];
      if (lng == null || lat == null) {
        setMapError('Location not found');
        setMapCenter(null);
        setMapOpen(true);
        return;
      }
      setMapCenter({ lat, lng });
      setMapOpen(true);
      // allow map to flyTo when modal renders
      setTimeout(() => {
        try {
          const map = mapRef.current?.getMap && mapRef.current.getMap();
          if (map && typeof map.flyTo === 'function') map.flyTo({ center: [lng, lat], zoom: 12 });
        } catch (err) {
          // ignore
        }
      }, 250);
    } catch (err) {
      setMapError('Geocode failed');
      setMapCenter(null);
      setMapOpen(true);
    } finally {
      setMapLoading(false);
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<LEStation | null>(null);

  const closeConfirm = () => { setConfirmOpen(false); setConfirmTarget(null); };
  const confirmToggle = async () => {
    if (!confirmTarget) return;
    // KAN-129: derive action from current status — same flow activates or deactivates.
    const nextActive = confirmTarget.status === 'Inactive';
    try {
      const resp = await setLEStationActive(confirmTarget.id, nextActive);
      if (resp && (resp.success || resp.message)) {
        setRowsData((r) => r.map((x) => (x.id === confirmTarget.id ? { ...x, status: nextActive ? 'Active' : 'Inactive' } : x)));
        setPowerState((s) => ({ ...s, [confirmTarget.stationName]: nextActive }));
        window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: resp?.message ?? (nextActive ? 'Station activated' : 'Station deactivated') } }));
      } else {
        window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: resp?.message ?? (nextActive ? 'Failed to activate station' : 'Failed to deactivate station') } }));
      }
    } catch (err: any) {
      console.error('[LEStations] Toggle active failed', err);
      window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: err?.message ?? (nextActive ? 'Failed to activate station' : 'Failed to deactivate station') } }));
    } finally {
      closeConfirm();
    }
  };

  const columns: GridColDef[] = [
    { field: 'stationName', headerName: 'Station Name', flex: 1.2, minWidth: 220, sortable: false, renderCell: (p) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ width: '32px', height: '32px', background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box component="img" src={leStationIcon} alt="LE Station" sx={{ width: '14px', height: '14px' }} />
        </Box>
        <Box><Typography sx={nodeNameSx}>{p.row.stationName}</Typography></Box>
      </Box>
    )},
    { field: 'primaryContact', headerName: 'Primary contact', flex: 0.8, minWidth: 150, sortable: false, renderCell: (p) => <Typography sx={nodeCitySx as any}>{p.row.primaryContact}</Typography> },
    { field: 'email', headerName: 'Contact email', flex: 0.8, minWidth: 150, sortable: false, renderCell: (p) => <Typography sx={nodeCitySx as any}>{p.row.email}</Typography> },
    { field: 'jurisdiction', headerName: 'Jurisdiction', flex: 0.6, minWidth: 120, sortable: false, renderCell: (p) => (
      <Box component="button" onClick={() => openJurisdictionMap(p.value)} sx={{ ...dispatcherSx, backgroundColor: '#F3F4F6', color: '#374151', px: 2, border: 'none', background: 'transparent', cursor: 'pointer' }}>
        {p.value}
      </Box>
    ) },
    { field: 'status', headerName: 'Status', flex: 0.6, minWidth: 130, sortable: false, renderCell: (p) => (p.value === 'Active' ? (<Box component="span" sx={activeTagSx}><Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: activeDotColor, top: '9.25px', left: '10px' }} /><Box component="span" sx={{ pl: '18px' }}>{p.value}</Box></Box>) : (<Box component="span" sx={inactiveTagSx}><Box sx={{ position: 'absolute', width: 6, height: 6, borderRadius: '9999px', bgcolor: inactiveDotColor, top: '9.25px', left: '10px' }} /><Box component="span" sx={{ pl: '18px' }}>{p.value}</Box></Box>)) },
    { field: 'actions', headerName: 'Actions', flex: 0.6, minWidth: 140, sortable: false, renderCell: (p) => {
      const key = p.row.stationName; const isInactive = p.row?.status === 'Inactive'; const iconColor = isInactive ? '#9CA3AF' : '#6B7280'; const showSlash = isInactive || !powerState[key];
      return (
        <Box>
          <IconButton size="small" onClick={() => { if (!isInactive) onEdit && onEdit(p.row); }} disabled={isInactive} aria-disabled={isInactive}><Box component="img" src={editIcon} alt="Edit" sx={{ width: '14px', height: '14px', opacity: isInactive ? 0.4 : 1 }} /></IconButton>
          {/* KAN-129: power toggle stays enabled for inactive rows to allow reactivation. */}
          <IconButton size="small" title={isInactive ? 'Activate' : 'Deactivate'} onClick={() => { setConfirmTarget(p.row); setConfirmOpen(true); }}>
            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <PowerSettingsNewIcon sx={{ color: iconColor, ...powerIconSx }} />
              {showSlash && <Box sx={{ position: 'absolute', width: '22px', height: '2px', bgcolor: iconColor, transform: 'rotate(45deg)' }} />}
            </Box>
          </IconButton>
        </Box>
      );
    }},
  ];

  return (
    <Box sx={pageBoxSx}>
      <Box mb={2}>
        <Box sx={headerRowSx}>
          <Box sx={leftTextSx}>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 700, fontSize: '20px', lineHeight: '30px' }}>Law Enforcement Stations</Typography>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '19.5px', color: '#6B7280' }}>Police stations that receive Level 3 incident alerts.</Typography>
          </Box>
          <Box sx={{ ...createButtonWrapperSx, position: 'relative' }}>
            <Button variant="outlined" sx={addAccountSX} onClick={() => onAdd && onAdd()}>+ Create station</Button>
            {notification && notification.show && (
              (() => {
                const isError = notification.severity === 'error';
                return (
                  <Box sx={{ position: 'absolute', bottom: 'calc(0%)', right: 0, width: '356px', height: '54px', borderRadius: '8px', backgroundColor: isError ? '#FEF2F2' : '#ECFDF3', border: `1px solid ${isError ? '#FCA5A5' : '#A9DEBF'}`, display: 'flex', alignItems: 'center', px: 2 }}>
                    <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: isError ? '#B91C1C' : '#008A2E', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                      {isError ? <svg width="13" height="13" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg> : <svg width="13" height="13" viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 3L3 5L7 1" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                    </Box>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px' }}>{notification.message}</Typography>
                  </Box>
                );
              })()
            )}
          </Box>
        </Box>

        <Box sx={controlsRowSx}>
          <Autocomplete
            size="small"
            sx={{ ...allSelectCombinedSx, minWidth: 160 }}
            options={['Active', 'Inactive']}
            value={selectedStatus}
            onChange={(_e, val) => setSelectedStatus(val)}
            renderInput={(params) => <TextField {...params} placeholder="All Statuses" />}
            clearOnEscape
          />
          <Autocomplete
            size="small"
            sx={{ ...allSelectCombinedSx, minWidth: 180 }}
            options={jurisdictionOptions}
            value={selectedJurisdiction}
            inputValue={jurisdictionSearch}
            onInputChange={(_e, val, reason) => {
              if (reason === 'input') {
                setJurisdictionSearch(val);
                if (jurisdictionDebounceRef.current) clearTimeout(jurisdictionDebounceRef.current);
                jurisdictionDebounceRef.current = setTimeout(() => {
                  setPaginationModel(m => ({ ...m, page: 0 }));
                }, 350);
              }
            }}
            onChange={(_e, val) => {
              setSelectedJurisdiction(val);
              setJurisdictionSearch(val ?? '');
              setPaginationModel(m => ({ ...m, page: 0 }));
            }}
            renderInput={(params) => <TextField {...params} placeholder="Search jurisdiction..." />}
            filterOptions={(x) => x}
            clearOnEscape
          />
        </Box>
      </Box>

      <Paper sx={nodeDetailsSx}>
        <div ref={gridContainerRef} style={{ height: '100%', width: '100%' }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            columnHeaderHeight={30}
            rowHeight={45}
            pagination
            paginationMode="server"
            pageSizeOptions={[5, 10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => { setPaginationModel(model); setPageSize(model.pageSize ?? pageSize); }}
            rowCount={rowCount}
            loading={loading}
            density="comfortable"
            disableRowSelectionOnClick
            sx={{ '& .MuiDataGrid-root': { position: 'relative' }, '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB !important', borderBottom: '1px solid rgba(0,0,0,0.06)', minHeight: '40px !important', maxHeight: '40px !important', position: 'sticky', top: 0, zIndex: 2 }, '& .MuiDataGrid-virtualScroller': { maxHeight: 'calc(100vh - 260px)', overflow: 'auto', paddingBottom: '56px' }, '& .MuiDataGrid-footerContainer': { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 4, background: '#FFFFFF', borderTop: '1px solid rgba(0,0,0,0.06)' }, '& .MuiDataGrid-columnHeader': { backgroundColor: '#F9FAFB !important', minHeight: '40px !important', maxHeight: '40px !important', display: 'flex', alignItems: 'center', borderRight: 'none' }, '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '11px', lineHeight: '16.5px', letterSpacing: '0.06px' }, '& .MuiDataGrid-columnSeparator': { display: 'none' }, '& .MuiDataGrid-cell': { borderRight: 'none' }, '& .MuiDataGrid-cellContent, & .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
          />
        </div>
      </Paper>

      <Modal open={confirmOpen} onClose={closeConfirm}>
        <Box sx={modalContainerSx}>
          <Box sx={modalHeaderSx}>
            <Typography sx={modalTitleSx}>{confirmTarget?.status === 'Inactive' ? 'Activate station?' : 'Deactivate station?'}</Typography>
            <IconButton size="small" onClick={closeConfirm} sx={modalCloseIconSx}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></IconButton>
          </Box>
          <Box sx={modalContentSx}>
            <Box sx={modalWarningRowSx}>
              <Box sx={{ width: '80px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', backgroundColor: '#FEF2F2', p: 0 }}>
                <Box component="img" src={AlertTriangle} sx={{ width: '16px', height: '16px' }} />
              </Box>
              <Box><Typography sx={modalBodyTextSx}>{confirmTarget?.status === 'Inactive' ? 'Activating this station will allow it to receive alerts again.' : 'Deactivated stations cannot receive alerts.'}</Typography></Box>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2}><Button sx={modalCancelButtonSx} onClick={closeConfirm}>Cancel</Button><Button sx={modalDeactivateButtonSx} onClick={() => { confirmToggle(); }}>{confirmTarget?.status === 'Inactive' ? 'Activate' : 'Deactivate'}</Button></Box>
          </Box>
        </Box>
      </Modal>
      {/* Jurisdiction map modal */}
      <Modal open={mapOpen} onClose={() => setMapOpen(false)}>
        <Box sx={{ position: 'absolute', width: '720px', maxWidth: '95%', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', borderRadius: '12px', backgroundColor: '#FFFFFF', outline: 'none', p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>Jurisdiction map</Typography>
            <IconButton size="small" onClick={() => setMapOpen(false)}><svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></IconButton>
          </Box>
          {mapError ? (
            <Box sx={{ p: 4 }}>{mapError}</Box>
          ) : (
            <Box sx={{ width: '100%', height: '480px' }}>
              {isValidMapboxToken ? (
                <Map
                  initialViewState={{ longitude: mapCenter?.lng ?? 0, latitude: mapCenter?.lat ?? 0, zoom: 2 }}
                  mapStyle={'mapbox://styles/mapbox/streets-v11'}
                  mapboxAccessToken={mapboxToken}
                  ref={mapRef}
                  style={{ width: '100%', height: '100%' }}
                >
                  {mapCenter && (
                    <Marker longitude={mapCenter.lng} latitude={mapCenter.lat} anchor="bottom">
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', background: '#39A9B1', border: '2px solid #fff' }} />
                    </Marker>
                  )}
                </Map>
              ) : (
                <Box sx={{ p: 4 }}>Map is disabled. Provide a valid public Mapbox token (REACT_APP_MAPBOX_TOKEN starting with "pk.").</Box>
              )}
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default LEStation;
